import { AxiosError, AxiosResponse } from "axios";
import { HttpStatus } from "constant";

export interface ApiResponse<T> {
    data: T;
    status: number;
}

export interface ApiError {
    error: {
        title: string;
        message: string;
    };
    status: number;
    data?: Record<string, string[]>;
}

export type ApiResult<T = unknown> = ApiResponse<T> | ApiError;

export function isApiError<T>(response: ApiResult<T>): response is ApiError {
    return response.status < 200 || response.status >= 300;
}

export interface ErrorResponseData {
    title?: string;
    message?: string;
    description?: string;
    errors?: Record<string, string[]>;
}

export class BaseService {
    protected handleSuccess<T>(response: AxiosResponse<T>): ApiResponse<T> {
        // Handle both response.data.data and response.data formats
        const responseData =
            response.data &&
                typeof response.data === "object" &&
                "data" in response.data &&
                response.data.data !== undefined
                ? response.data.data
                : response.data;

        return {
            data: responseData as T,
            status: response.status,
        };
    }

    protected handleError(error: AxiosError): ApiError {
        const errorData = error.response?.data as ErrorResponseData | undefined;
        const status =
            error.response?.status ?? HttpStatus.INTERNAL_SERVER_ERROR;

        const title = errorData?.title ?? "Error";
        const message =
            errorData?.message ??
            errorData?.description ??
            error.message ??
            "An error occurred";

        return {
            error: {
                title,
                message,
            },
            ...(errorData?.errors && { data: errorData.errors }),
            status,
        };
    }
}
